import { useMemo } from 'react'
import { useArenaPostAuthors } from './api'
import { Select } from '@mantine/core'
import {
  IconCalendar,
  IconChevronDown,
  IconUserSearch,
  IconZoom,
} from '@tabler/icons-react'
import classes from './Arena.module.css'
import { DatePickerInput } from '@mantine/dates'
import { useCompetitionId } from '../common/hooks'
import { Input } from '../common/components/input/StyledTextInput'

interface ArenaSearchInputProps {
  onSearch: (value: string | undefined) => void
}

export function ArenaSearchInput({ onSearch }: ArenaSearchInputProps) {
  return (
    <Input
      placeholder="Search"
      rightSection={<IconZoom className={classes.searchIcon} />}
      rightSectionPointerEvents="none"
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          const value = e.currentTarget.value.trim()
          onSearch(value || undefined)
        }
      }}
    />
  )
}

export interface ArenaPostsFilterType {
  author?: number
  created_after?: string
  created_before?: string
  search?: string
}

interface ArenaPostsFiltersProps {
  onChange: (postsFilter: ArenaPostsFilterType) => void
  author: number | undefined
  setAuthor: React.Dispatch<React.SetStateAction<number | undefined>>
  dateRange: [string | null, string | null]
  setDateRange: React.Dispatch<
    React.SetStateAction<[string | null, string | null]>
  >
}

export default function ArenaPostFilters({
  onChange,
  author,
  setAuthor,
  dateRange,
  setDateRange,
}: ArenaPostsFiltersProps) {
  const competitionId = useCompetitionId()

  const updateFilters = (updates: Partial<ArenaPostsFilterType>) => {
    const currentFilters: ArenaPostsFilterType = {
      author,
      created_after: dateRange[0] ? dateRange[0] : undefined,
      created_before: dateRange[1] ? dateRange[1] : undefined,
    }

    onChange({ ...currentFilters, ...updates })
  }

  return (
    <>
      <AuthorSelect
        value={author}
        onChange={(newAuthor) => {
          setAuthor(newAuthor)
          updateFilters({ author: newAuthor })
        }}
        competition_id={competitionId}
      />
      <DatePickerInput
        classNames={{ input: classes.input, placeholder: classes.placeholder }}
        clearable
        type="range"
        placeholder="Date posted"
        leftSection={
          <IconCalendar className={classes.arenaPostsFilterLeftIcon} />
        }
        rightSection={
          <IconChevronDown className={classes.arenaPostsFilterRightIcon} />
        }
        leftSectionPointerEvents="none"
        rightSectionPointerEvents="none"
        value={dateRange}
        onChange={(period) => {
          setDateRange(period)
          if (period?.[0] != null && period?.[1] != null) {
            updateFilters({
              created_after: period[0],
              created_before: period[1],
            })
          } else if (period?.[0] == null && period?.[1] == null) {
            updateFilters({
              created_after: undefined,
              created_before: undefined,
            })
          }
        }}
        popoverProps={{
          withinPortal: true,
          zIndex: 400,
          classNames: { dropdown: classes.dropdown },
        }}
      />
    </>
  )
}

interface AuthorSelectProps {
  value?: number
  onChange: (value: number | undefined) => void
  competition_id: number
}

export function AuthorSelect({
  value,
  onChange,
  competition_id,
}: AuthorSelectProps) {
  const { data: authors } = useArenaPostAuthors({
    competition_id: competition_id,
  })

  const selectData = useMemo(() => {
    return authors.map((author) => ({
      value: author.user_id.toString(),
      label: author.name,
    }))
  }, [authors])

  const handleChange = (val: string | null) => {
    onChange(val ? parseInt(val, 10) : undefined)
  }

  return (
    <Select
      classNames={{ input: classes.authorSelect, dropdown: classes.dropdown }}
      data={selectData}
      value={value?.toString() ?? null}
      onChange={handleChange}
      searchable
      placeholder="Published by"
      leftSection={
        <IconUserSearch className={classes.arenaPostsFilterLeftIcon} />
      }
      clearable
      nothingFoundMessage="No authors found"
      comboboxProps={{ withinPortal: false, zIndex: 400 }}
    />
  )
}
